import React, { useEffect, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { showToast } from './NotificationTray';

// Export state so other components can use it
export const useUpdateAvailable = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const handleUpdate = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    const serverVersion = localStorage.getItem('latestServerVersion');
    if (serverVersion) {
      localStorage.setItem('appVersion', serverVersion);
    }
    window.location.reload();
  };
  
  return { updateAvailable, setUpdateAvailable, handleUpdate };
};

export default function UpdateNotification() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(() => localStorage.getItem('appVersion'));
  const [hasNotified, setHasNotified] = useState(false);
  
  useEffect(() => {
    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Check for updates periodically
        const checkForUpdates = () => {
          registration.update().catch(console.error);
        };
        
        // Check every 5 minutes
        const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
        
        // Listen for update found
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is ready to activate
              setUpdateAvailable(true);
              if (!hasNotified) {
                showToast(
                  'Update Available',
                  'A new version is ready. Refresh when convenient.',
                  'info'
                );
                setHasNotified(true);
              }
            }
          });
        });
        
        // Clean up
        return () => clearInterval(interval);
      });
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'SERVICE_WORKER_UPDATED') {
          setUpdateReady(true);
          if (!hasNotified) {
            showToast(
              'Update Ready',
              'The app will refresh automatically in 30 seconds, or refresh now.',
              'info'
            );
            setHasNotified(true);
          }
          
          // Never auto-refresh, let the user decide when to update
          // This prevents killing active uploads or work in progress
          console.log('Update ready, user can refresh when convenient');
        }
      });
    }
  }, [hasNotified]);

  // Poll /api/version and trigger UI if version changes
  useEffect(() => {
    let timer;
    const fetchVersion = async () => {
      try {
        const res = await fetch('/api/version', { credentials: 'include', cache: 'no-store' });
        const data = await res.json();
        setServerVersion(data.version);
        localStorage.setItem('latestServerVersion', data.version);
        const prev = localStorage.getItem('appVersion');
        if (prev && prev !== data.version) {
          setUpdateAvailable(true);
          if (!hasNotified) {
            showToast(
              'Update Available',
              `Version ${data.version} is ready. Refresh when convenient.`,
              'info'
            );
            setHasNotified(true);
          }
        }
        if (!prev && data.version) {
          localStorage.setItem('appVersion', data.version);
          setCurrentVersion(data.version);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchVersion();
    timer = setInterval(fetchVersion, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [hasNotified]);
  
  const handleUpdate = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Tell the service worker to skip waiting
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    if (serverVersion) {
      localStorage.setItem('appVersion', serverVersion);
    }
    window.location.reload();
  };
  
  // Store update state globally so other components can access it
  useEffect(() => {
    window.__updateAvailable = updateAvailable;
    window.__handleUpdate = handleUpdate;
  }, [updateAvailable]);
  
  // No visual component - all notifications go through the NotificationTray
  return null;
}

// Optional: Refresh button component that can be added to header/nav
export function UpdateRefreshButton() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  
  useEffect(() => {
    const checkUpdate = () => {
      setUpdateAvailable(window.__updateAvailable || false);
    };
    checkUpdate();
    const interval = setInterval(checkUpdate, 5000);
    return () => clearInterval(interval);
  }, []);
  
  if (!updateAvailable) return null;
  
  const handleClick = () => {
    if (window.__handleUpdate) {
      window.__handleUpdate();
    } else {
      window.location.reload();
    }
  };
  
  return (
    <button 
      onClick={handleClick}
      className="update-refresh-btn"
      title="Update available - click to refresh"
      aria-label="Refresh to update"
    >
      <FiRefreshCw className="refresh-icon" />
      <span className="update-badge">1</span>
    </button>
  );
}